import { useState, useEffect } from "react";
// Custom components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import DynamicTable from "@Washouts/components/tables/Common/DynamicTable";
// MUI
import { Box } from "@mui/material";
// Types
import { GridValidRowModel } from "@mui/x-data-grid-premium";
import { DepartmentalWashoutsTableProps } from "@/Washouts/types/sales";
import { DynamicColDef } from "@/Washouts/types/commons";
import { DEFAULT_HEADER_HEIGHT, HEADER_CLASS_NAME } from "@/Washouts/constants";
import { StringMap } from "@/Common/interfaces/types";

// Columns
import { getTableColumnsMap } from "./columns";

const SUB_FOOTER_DEFAULT_CLASS = "bg-[#b3b3b3]";
const FOOTER_DEFAULT_CLASS = "aggregation-footer fs-14";
const FONT_SIZE_IMPORTANT = "17px !important";

const footerTitles: StringMap<string> = {
	financeGross: "Total F&I Gross",
	preOwnedCarsSales: "Total Used Front Gross",
	serviceDepartment: "Total Service Gross",
	partsDepartment: "Total Parts Gross",
	bodyShopDepartment: "Total Body Shop Gross",
	variableOperationsCosts: "Total Variable Ops Cost",
	fixedOperationsCosts: "Total Fixed Ops Cost",
	emptyTable: "",
};

const columnTitles: StringMap<string> = {
	financeGross: "F&I Protection Plan Activity",
	preOwnedCarsSales: "Pre-Owned Vehicle Sales",
	serviceDepartment: "Mechanical",
	partsDepartment: "Parts & Accessories",
	bodyShopDepartment: "Body Shop",
	variableOperationsCosts: "Ops Costs",
	fixedOperationsCosts: "Ops Costs",
};

export default function DepartmentalWashoutsTable(props: Readonly<DepartmentalWashoutsTableProps>) {
	const { tableRef = "", loading = false } = props;

	const [rows, setRows] = useState<GridValidRowModel[]>([]);
	const [columns, setColumns] = useState<DynamicColDef[]>([]);

	const [styles, setStyles] = useState({
		headerClassName: "table-header-top-aligned table-header table-small-header show-as-column header-pink",
		tableContainerStyle: { height: "100%", width: "100%", fontSize: FONT_SIZE_IMPORTANT },
		subFooterClassName: SUB_FOOTER_DEFAULT_CLASS,
		aggregationFooterClassName: FOOTER_DEFAULT_CLASS,
	});

	const labelHeaderTitle = columnTitles?.[tableRef] ?? "";
	const labelFooterTitle = footerTitles?.[tableRef] ?? "Total Gross";

	const tableColumnsMap = getTableColumnsMap(props, labelHeaderTitle, labelFooterTitle);

	const getColumnById = (id: unknown) => tableColumnsMap.get(String(id));

	const getColumns = (tableReference = "") => {
		let columnsAux: DynamicColDef[] = [];
		if (tableReference === "financeGross") {
			columnsAux = [
				getColumnById("label"),
				getColumnById("account"),
				getColumnById("contracts"),
				getColumnById("grossProfit"),
				getColumnById("icon"),
			].filter((column) => column !== undefined);
		} else if (tableReference === "preOwnedCarsSales") {
			columnsAux = [
				getColumnById("label"),
				getColumnById("account"),
				getColumnById("units"),
				getColumnById("grossProfit"),
				getColumnById("icon"),
			].filter((column) => column !== undefined);
		} else if (
			["variableOperationsCosts", "partsDepartment", "serviceDepartment", "bodyShopDepartment", "fixedOperationsCosts"].includes(
				tableReference
			)
		) {
			columnsAux = [getColumnById("label"), getColumnById("account"), getColumnById("grossProfit"), getColumnById("icon")].filter(
				(column) => column !== undefined
			);
		} else if (tableReference === "emptyTable") {
			columnsAux = [getColumnById("emptyColumn")].filter((column) => column !== undefined);
		}
		return columnsAux;
	};

	const getStyles = () => {
		return {
			subFooterClassName: SUB_FOOTER_DEFAULT_CLASS,
			aggregationFooterClassName: FOOTER_DEFAULT_CLASS,
			headerClassName: HEADER_CLASS_NAME,
			tableContainerStyle: { height: "100%", width: "100%", fontSize: FONT_SIZE_IMPORTANT },
		};
	};

	useEffect(() => {
		if (props?.tableRef) {
			const rowsAux = props?.rows ?? [];
			setRows(rowsAux);
			setColumns(getColumns(props?.tableRef));
			setStyles(getStyles());
		}
		return () => {
			setRows([]);
			setColumns([]);
			setStyles(getStyles());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props]);

	if (loading) {
		return <TableLoader smallTitle content sx={{ padding: "15px 10px 0px 10px" }} rows={15} />;
	}

	return (
		<>
			{props.dataPresent && (
				<>
					{!props?.title && <Box sx={{ height: "64.5px" }}></Box>}
					<DynamicTable
						{...props}
						tableRef={tableRef}
						headerHeight={DEFAULT_HEADER_HEIGHT}
						columns={columns}
						columnsToExclude={["pvr", "subFooter"]}
						data={rows}
						title={{
							hide: false,
							label: props?.title,
							sx: { mb: "8px" },
						}}
						tableContainerStyle={{ height: "100%", width: "100%", fontSize: FONT_SIZE_IMPORTANT, paddingTop: "12px" }}
						subFooterClassName={styles?.subFooterClassName}
						negatives={[
							"units",
							"retailUnits",
							"leaseUnits",
							"grossProfit",
							"retailGrossProfit",
							"leaseGrossProfit",
							"account",
							"contracts",
						]}
						disableToolbar={tableRef === "emptyTable"}
						customProps={{
							tableClassName: {
								"[&_.full-collapse-toolbar>.MuiStack-root>.MuiStack-root]:min-w-full": true,
								"[&_.page-title]:mb-[35px]": true,
								"[&_.toolbar]:absolute [&_.toolbar-actions]:right-0 [&_.toolbar-actions]:top-[38px]": true,
								// Ensure a top border is displayed when the table is empty to keep visual consistency
								"[&_.MuiDataGrid-toolbarContainer]:shadow-[inset_0_-1px_0_0_var(--DataGrid-rowBorderColor)]":
									props.tableRef === "emptyTable",
							},
						}}
					/>
				</>
			)}
		</>
	);
}
